import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {LabelList} from "recharts";
import {getFallHistogram} from "../../../../selectors/animalDocumentsSelectors";
import Chart from "../../../basics/chart/Chart";
import Button from "../../../basics/button/Button";
import Menu from "../../../basics/menu/Menu";
import MenuItem from "../../../basics/menu/MenuItem";

export default function FallHistogram() {
    const {t} = useTranslation();

    const [sortEvent, setSortEvent] = useState(null);

    const [sortBy, setSortBy] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");

    const data = useSelector(getFallHistogram);

    const sortedData = useMemo(() => {
        if (!data) return [];
        const copy = data.slice();
        copy.sort((a, b) => {
            let compareValue;
            if (sortBy === "name") {
                compareValue = a.reason.localeCompare(b.reason);
            } else {
                const aValue = a.porkhouse + a.piglethouse + a.other;
                const bValue = b.porkhouse + b.piglethouse + b.other;
                compareValue = aValue - bValue;
            }
            return sortDirection === "asc" ? compareValue : -compareValue;
        });
        return copy;
    }, [data, sortBy, sortDirection]);

    const customLabel = (props) => {
        return props.data.map((item) => {
            return <LabelList position={"top"} />;
        });
    };
    const dataDef = useMemo(
        () => [
            {
                dataKey: "porkhouse",
                color: "green",
                name: t("SType.5"),
                stack: "fall",
            },
            {
                dataKey: "piglethouse",
                color: "blue",
                name: t("SType.6"),
                stack: "fall",
            },
            {
                dataKey: "other",
                color: "yellow",
                name: t("farms.tabs.mapTab.types.OTHER"),
                custom: customLabel,
                stack: "fall",
            },
        ],
        [t]
    );

    const onSortColumnClick = useCallback(
        (column) => {
            if (sortBy === column) {
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
                return;
            }

            setSortBy(column);
            setSortDirection("asc");
        },
        [sortBy]
    );

    const getSortIcon = useCallback(
        (column) => {
            if (sortBy !== column) {
                return <i className="fa-solid fa-fw" />;
            }

            return (
                <i
                    className={`fa-solid fa-arrow-${sortDirection === "asc" ? "up" : "down"}-wide-short fa-fw`}
                />
            );
        },
        [sortBy, sortDirection]
    );

    return (
        <div>
            <div className="text-end">
                <Button
                    onClick={setSortEvent}
                    icon={
                        <i
                            className={`fa-solid fa-arrow-${sortDirection === "asc" ? "up" : "down"}-wide-short fa-fw`}
                        />
                    }>
                    {sortBy === "name" ? t("sortByValue") : t("sortByName")}
                </Button>
            </div>
            <div className={"h-0 mh-15rem"}>
                <Chart
                    Yaxis={{
                        name: t("objectDescription.ANIMAL.AnmCnt._title"),
                    }}
                    Xaxis={{
                        dataKey: "reason",
                        name: t("reason"),
                    }}
                    data={sortedData}
                    type={"Bar"}
                    isAnimationActive={false}
                    dataDef={dataDef}
                    saveAsExcell={t("fallHistogram")}
                />
            </div>
            <Menu event={sortEvent}>
                <MenuItem
                    onClick={() => onSortColumnClick("name")}
                    icon={getSortIcon("name")}>
                    {t("sortByName")}
                </MenuItem>
                <MenuItem
                    onClick={() => onSortColumnClick("value")}
                    icon={getSortIcon("value")}>
                    {t("sortByValue")}
                </MenuItem>
            </Menu>
        </div>
    );
}
